/**
 * 鼠标改键 — 静态配置与类型映射
 *
 * 本文件包含：
 * 1. 鼠标物理按键配置（MOUSE_KEY_ITEMS）
 * 2. 改键功能列表（BASIC_KEYS / MEDIA_KEYS）及其索引 Map
 * 3. SDK ↔ Keycode 类型枚举与映射表
 * 4. 鼠标按键默认 keywords 与 index 的双向映射
 * 5. 鼠标按键 code 与 bit 位的双向映射
 */

// ============ 鼠标物理按键配置 =================

type MouseKey = {
  key: number
  keywords: string
  name: string
  url: string
}

/**
 * 鼠标按键通用配置
 * 鼠标按键的按钮名称和按钮功能区域图片
 */
const MOUSE_KEY_ITEMS: MouseKey[] = [
  { key: 0, keywords: '左击', name: 'keys.normal.leftClick', url: 'mouse left.png' },
  { key: 1, keywords: '中键', name: 'keys.normal.middleClick', url: 'roller.png' },
  { key: 2, keywords: '右击', name: 'keys.normal.rightClick', url: 'mouse right.png' },
  { key: 3, keywords: 'DPI+', name: 'keys.normal.dpiInc', url: 'dpi+.png' },
  { key: 4, keywords: 'DPI-', name: 'keys.normal.dpiDec', url: 'dpi-.png' },
  { key: 5, keywords: '右侧', name: 'keys.normal.forward', url: 'mouse forward.png' },
  { key: 6, keywords: '左侧', name: 'keys.normal.back', url: 'mouse back.png' },
  { key: 9, keywords: '上滚', name: 'keys.normal.wheelForward', url: 'roller.png' },
  { key: 10, keywords: '下滚', name: 'keys.normal.wheelBackward', url: 'roller.png' },
  { key: 13, keywords: 'DIY', name: 'keys.normal.diy', url: 'diy.png' },
]

/** 鼠标按键通用配置索引 `keywords -> MouseKey` */
export const MOUSE_KEY_ITEMS_MAP = new Map<string, MouseKey>()
for (const item of MOUSE_KEY_ITEMS) {
  if (!MOUSE_KEY_ITEMS_MAP.has(item.keywords)) {
    MOUSE_KEY_ITEMS_MAP.set(item.keywords, item)
  }
}

// ============ 改键功能列表 =================

export interface BasicKey {
  labelKey: string
  keywords: string
  icon: string
}

/**
 * 鼠标常规改键功能列表（TabNormal 面板）
 */
export const BASIC_KEYS: BasicKey[] = [
  { keywords: '左击', labelKey: 'keys.normal.leftClick', icon: 'VirtualKey-Wireframe-Left' },
  { keywords: '中键', labelKey: 'keys.normal.middleClick', icon: 'VirtualKey-Wireframe-Middle' },
  { keywords: '右击', labelKey: 'keys.normal.rightClick', icon: 'VirtualKey-Wireframe-Right' },
  {
    keywords: '上滚',
    labelKey: 'keys.normal.wheelForward',
    icon: 'VirtualKey-Wireframe-Rollerforward',
  },
  {
    keywords: '下滚',
    labelKey: 'keys.normal.wheelBackward',
    icon: 'VirtualKey-Wireframe-Rollerback',
  },
  { keywords: '左滚', labelKey: 'keys.normal.wheelLeft', icon: 'VirtualKey-Wireframe-Rollerleft' },
  {
    keywords: '右滚',
    labelKey: 'keys.normal.wheelRight',
    icon: 'VirtualKey-Wireframe-Rollerright',
  },
  { keywords: '右侧', labelKey: 'keys.normal.forward', icon: 'VirtualKey-Wireframe-Forward' },
  { keywords: '左侧', labelKey: 'keys.normal.back', icon: 'VirtualKey-Wireframe-Back' },
  { keywords: 'DPI+', labelKey: 'keys.normal.dpiInc', icon: 'VirtualKey-Wireframe-dpiInc' },
  { keywords: 'DPI-', labelKey: 'keys.normal.dpiDec', icon: 'VirtualKey-Wireframe-dpiDec' },
  { keywords: 'DIY', labelKey: 'keys.normal.diy', icon: 'VirtualKey-Wireframe-diy' },
  {
    keywords: 'Fire button',
    labelKey: 'keys.normal.fireButton',
    icon: 'VirtualKey-Wireframe-firekey',
  },
  {
    keywords: 'Sniper button',
    labelKey: 'keys.normal.sniperButton',
    icon: 'VirtualKey-Wireframe-snipe',
  },
  {
    keywords: 'DPI cycle+',
    labelKey: 'keys.normal.dpiCycleInc',
    icon: 'VirtualKey-Wireframe-dpicycleInc',
  },
  {
    keywords: 'DPI cycle-',
    labelKey: 'keys.normal.dpiCycleDec',
    icon: 'VirtualKey-Wireframe-dpicycleDec',
  },
  {
    keywords: 'Config cycle',
    labelKey: 'keys.normal.configCycle',
    icon: 'VirtualKey-Wireframe-cfgswitch',
  },
  {
    keywords: 'Dongle cycle',
    labelKey: 'keys.normal.dongleCycle',
    icon: 'Hardware-Wireframe-Receiver',
  },
  { keywords: 'Disable key', labelKey: 'keys.normal.disableKey', icon: 'Function-Wireframe-Close' },
]

/** 改键功能 keywords → 可读 label 索引 */
export const BASIC_KEYS_LABEL_MAP = new Map<string, string>()
for (const item of BASIC_KEYS) {
  BASIC_KEYS_LABEL_MAP.set(item.keywords, item.labelKey)
}

export interface MediaKey {
  labelKey: string
  keywords: string
  keyType: string
  icon: string
}

/**
 * 多媒体改键功能列表（TabMedia 面板）
 *
 * 数据来源：C++ 项目 kb_common.json 的 mouseMultimedia。
 * 注意：部分按键（Close window、Lock computer、Copy 等）在 keycode.json 中 type=15，
 * 本质是键盘组合键（SDK type=0x02），但在 UI 上归类为多媒体功能。
 */
export const MEDIA_KEYS: MediaKey[] = [
  {
    keywords: '|◄◄',
    labelKey: 'keys.media.prevTrack',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-PrevTrack',
  },
  {
    keywords: '►||',
    labelKey: 'keys.media.playPause',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-PrevTrack-1',
  },
  {
    keywords: '►►|',
    labelKey: 'keys.media.nextTrack',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-NextTrack',
  },
  {
    keywords: 'Stop',
    labelKey: 'keys.media.stop',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-Stop',
  },
  {
    keywords: '📢✕',
    labelKey: 'keys.media.mute',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-Mute',
  },
  {
    keywords: '📢➕',
    labelKey: 'keys.media.volumeUp',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-Vol1',
  },
  {
    keywords: '📢➖',
    labelKey: 'keys.media.volumeDown',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-Vol2',
  },
  {
    keywords: 'Music',
    labelKey: 'keys.media.player',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-Player',
  },
  {
    keywords: '✉',
    labelKey: 'keys.media.mail',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-Mail',
  },
  {
    keywords: '📱',
    labelKey: 'keys.media.calculator',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-Calc1',
  },
  {
    keywords: 'PC',
    labelKey: 'keys.media.myComputer',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-MyPC',
  },
  {
    keywords: 'Browser_Search',
    labelKey: 'keys.media.search',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-Search',
  },
  {
    keywords: 'Close window',
    labelKey: 'keys.media.closeWindow',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-CloseWindow',
  },
  {
    keywords: 'Lock computer',
    labelKey: 'keys.media.lockComputer',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-LockPC',
  },
  {
    keywords: 'Run',
    labelKey: 'keys.media.application',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-app',
  },
  {
    keywords: 'Show desktop',
    labelKey: 'keys.media.showDesktop',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-ShowDesktop',
  },
  {
    keywords: 'Zoom in',
    labelKey: 'keys.media.zoomInScreen',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-ScreenZoomIn',
  },
  {
    keywords: 'Zoom out',
    labelKey: 'keys.media.zoomOutScreen',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-ScreenZoomOut',
  },
  {
    keywords: 'Copy',
    labelKey: 'keys.media.copy',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-Copy',
  },
  {
    keywords: 'Paste',
    labelKey: 'keys.media.paste',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-Paste',
  },
  {
    keywords: 'Cut',
    labelKey: 'keys.media.cut',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-Cut',
  },
  {
    keywords: 'Select all',
    labelKey: 'keys.media.selectAll',
    keyType: 'media',
    icon: 'Multimedia-Wireframe-SelectAll',
  },
]

/** 多媒体改键功能 keywords → i18n labelKey 索引 */
export const MEDIA_KEYS_LABEL_MAP = new Map<string, string>()
for (const item of MEDIA_KEYS) {
  MEDIA_KEYS_LABEL_MAP.set(item.keywords, item.labelKey)
}

// ============ SDK ↔ Keycode 类型枚举 =================

/**
 * SDK keyAttr.type 枚举（设备协议层的功能模式标识）
 */
export enum SdkKeyAttrType {
  /** 键盘单键 */
  Keyboard = 0x00,
  /** 键盘组合键（含预定义快捷键如 Lock computer，和用户自定义组合键） */
  CombinedKey = 0x02,
  /** 鼠标按键 */
  MouseButton = 0x03,
  /** 多媒体键（Consumer/System Control） */
  Media = 0x04,
  /** 宏定义 */
  Macro = 0x05,
  /** 禁用按键 */
  Disable = 0x07,
  /** DPI */
  DPI = 0x08,
  /** 功能键（狙击/火力） */
  Function = 0x09,
  /** 自定义切换键 */
  DIY = 0x0a,
  /** 垂直滚轮 */
  VerticalWheel = 0x0b,
  /** 水平滚轮 */
  HorizontalWheel = 0x0c,
  /** 任意键盘组合（无修饰键区分） */
  ArbitraryCombined = 0x0d,
  /** 默认（未改键） */
  Default = 0xff,
}

/**
 * Keycode 元数据 type 枚举（keycode.json 中的分类标识）
 *
 * 对应 keycode.json 每条记录的 "type" 字段值。
 */
export enum KeycodeMetaType {
  /** 禁用键 */
  Disable = 0x00,
  /** 键盘单键 */
  Keyboard = 0x01,
  /** 多媒体键 - Consumer Control（播放/音量/浏览器等） */
  MediaA = 0x02,
  /** 多媒体键 - System Control（Power/Sleep/Wake） */
  MediaB = 0x03,
  /** 鼠标虚拟键 */
  MouseButton = 0x04,
  /** 垂直滚轮（上滚 code=0x01 / 下滚 code=0xff） */
  WheelVertical = 0x06,
  /** 水平滚轮（右滚 code=0x01 / 左滚 code=0xff） */
  WheelHorizontal = 0x07,
  /** 宏 */
  Macro = 0x08,
  /** 预定义组合键快捷方式（Lock computer、Copy 等） */
  CombinedKey = 0x0f,
  /** DPI */
  DPI = 50,
  /** 功能键（狙击/火力） */
  Function = 51,
  /** 自定义切换键 */
  DIY = 52,
  /** 默认（恢复） */
  Default = 0xff,
}

// ============ SDK ↔ Keycode 类型映射表 =================

/**
 * 单条 SDK ↔ Keycode 类型映射规则。
 *
 * - sdkType: SDK keyAttr.type 值
 * - metaTypes: 对应的 keycode.json 元数据 type 值（可能有多个候选，按优先级排列）
 * - structured: 是否为结构化改键（宏/组合键），不走 code 映射查询
 */
export interface KeyTypeMapping {
  sdkType: SdkKeyAttrType
  metaTypes: KeycodeMetaType[]
  structured?: boolean
}

/**
 * SDK keyAttr.type ↔ Keycode meta type 的完整映射表。
 *
 * 用途：
 * - SDK → Setting（回显）：根据 sdkType 找到 metaTypes，构造查询候选
 * - Setting → SDK（下发）：根据 metaType 反查 sdkType，构造 SDK payload
 *
 * structured 类型没有目标键的信息，不通过 code 映射查询，需要特殊处理路径。
 */
export const KEY_TYPE_MAPPINGS: KeyTypeMapping[] = [
  { sdkType: SdkKeyAttrType.Keyboard, metaTypes: [KeycodeMetaType.Keyboard] },
  {
    sdkType: SdkKeyAttrType.CombinedKey,
    metaTypes: [KeycodeMetaType.CombinedKey],
    structured: true,
  },
  { sdkType: SdkKeyAttrType.MouseButton, metaTypes: [KeycodeMetaType.MouseButton] },
  { sdkType: SdkKeyAttrType.Media, metaTypes: [KeycodeMetaType.MediaA, KeycodeMetaType.MediaB] },
  { sdkType: SdkKeyAttrType.Macro, metaTypes: [KeycodeMetaType.Macro], structured: true },
  { sdkType: SdkKeyAttrType.Disable, metaTypes: [KeycodeMetaType.Disable] },
  { sdkType: SdkKeyAttrType.DPI, metaTypes: [KeycodeMetaType.DPI] },
  { sdkType: SdkKeyAttrType.Function, metaTypes: [KeycodeMetaType.Function] },
  { sdkType: SdkKeyAttrType.DIY, metaTypes: [KeycodeMetaType.DIY] },
  { sdkType: SdkKeyAttrType.VerticalWheel, metaTypes: [KeycodeMetaType.WheelVertical] },
  { sdkType: SdkKeyAttrType.HorizontalWheel, metaTypes: [KeycodeMetaType.WheelHorizontal] },
  { sdkType: SdkKeyAttrType.ArbitraryCombined, metaTypes: [], structured: true },
  { sdkType: SdkKeyAttrType.Default, metaTypes: [KeycodeMetaType.Default] },
]

/** SDK keyAttr.type → 映射规则索引 */
export const SDK_TO_META_MAP = new Map<SdkKeyAttrType, KeyTypeMapping>()
for (const mapping of KEY_TYPE_MAPPINGS) {
  SDK_TO_META_MAP.set(mapping.sdkType, mapping)
}

/** Keycode meta type → SDK keyAttr.type 索引（反向查询） */
export const META_TO_SDK_MAP = new Map<KeycodeMetaType, SdkKeyAttrType>()
for (const mapping of KEY_TYPE_MAPPINGS) {
  for (const metaType of mapping.metaTypes) {
    META_TO_SDK_MAP.set(metaType, mapping.sdkType)
  }
}

// ============ 鼠标按键 index ↔ keywords 映射 =================

/**
 * 鼠标按键 index → 默认 keywords 映射
 * 当设备 manifest 中未定义 layout.keys 时的回退表。
 */
export const DEFAULT_MOUSE_KEYWORDS_BY_INDEX: Record<number, string> = {
  0: '左击',
  1: '中键',
  2: '右击',
  3: 'DPI+',
  4: 'DPI-',
  5: '右侧',
  6: '左侧',
  9: '上滚',
  10: '下滚',
  11: '右滚',
  12: '左滚',
  13: 'DIY',
}

/** 鼠标按键 keywords → index 反向映射 */
export const DEFAULT_MOUSE_INDEX_BY_KEYWORDS: Record<string, number> = Object.fromEntries(
  Object.entries(DEFAULT_MOUSE_KEYWORDS_BY_INDEX).map(([index, keywords]) => [
    keywords,
    Number(index),
  ]),
)

// ============ 鼠标按键 code ↔ bit 位映射 =================

/**
 * 鼠标按键 bit 位 → HID code 映射
 * SDK mouseKey 数组中 bit 位为 1 的索引对应的虚拟键码。
 */
export const MOUSE_KEY_TO_CODE: Record<number, number> = {
  0: 0xf0,
  1: 0xf1,
  2: 0xf2,
  3: 0xf3,
  4: 0xf4,
}

/** HID code → 鼠标按键 bit 位反向映射 */
export const CODE_TO_MOUSE_BIT: Record<number, number> = Object.fromEntries(
  Object.entries(MOUSE_KEY_TO_CODE).map(([bit, code]) => [code, Number(bit)]),
)
